if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.loadingSpinner = this.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.cartRecoveryPending || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const checkoutAfterAdd = evt.submitter?.dataset?.checkoutAfterAdd === 'true';
        let addedToCart = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // The form snapshot above must stay synchronous: product UI observers
        // disable quantity/variant controls while this request waits its turn.
        const add = () => fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const state = await response.json();
            if (response.ok === false && !state.status) throw new Error(window.cartStrings.error);
            return state;
          })
          .then((response) => {
            if (response.status) {
              throw Object.assign(new Error(response.description || window.cartStrings.error), { cartAddResponse: response });
            }
            addedToCart = true;
            return response;
          });
        const coordinated = Boolean(window.YBCartMutations?.runAdd);
        const request = coordinated ? window.YBCartMutations.runAdd(add) : add();
        request
          .catch((error) => {
            // Keep the native product error UI while concurrent checkout waiters
            // receive the rejected add lease and cannot continue without it.
            if (error.cartAddResponse) return error.cartAddResponse;
            throw error;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || window.cartStrings.error);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            addedToCart = true;
            const updatedCountry = coordinated && window.YBCartMutations.changedCountry();
            if (updatedCountry) { window.YBCartMutations.reloadForCountry(updatedCountry); return; }
            if (checkoutAfterAdd) {
              const root = window.Shopify?.routes?.root || '/';
              const country = [window.YB_AUTO_LOCALIZATION?.currentCountry, window.Shopify?.country]
                .map((value) => String(value || '').trim().toUpperCase())
                .find((value) => /^[A-Z]{2}$/.test(value));
              const checkoutUrl = new URL(`${root.replace(/\/$/, '')}/cart/checkout`, window.location.origin);
              if (country) checkoutUrl.searchParams.set('checkout[shipping_address][country]', country);
              window.location = checkoutUrl.pathname + checkoutUrl.search;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.renderAddedCart(response, coordinated);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              return this.renderAddedCart(response, coordinated);
            }
          })
          .catch((e) => {
            if (e.cartContextChanged) return;
            console.error(e);
            if (addedToCart) {
              this.recoverAddedCart();
              return;
            }
            this.handleErrorMessage(window.cartStrings.error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (addedToCart && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && !this.cartRecoveryPending) this.submitButton.removeAttribute('aria-disabled');
            this.loadingSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      renderAddedCart(response, coordinated = false) {
        if (coordinated) {
          // The lease already painted the authoritative cart. An older bundled
          // add section, especially after a modal closes, must never paint over it.
          return window.YBCartMutations.flush().then(() => {
            const country = window.YBCartMutations.changedCountry();
            if (country) { window.YBCartMutations.reloadForCountry(country); return; }
            if (this.cart.tagName !== 'CART-DRAWER') { this.recoverAddedCart(); return; }
            this.cart.open(undefined, () => this.recoverAddedCart());
          }).catch((error) => {
            console.error(error);
            this.recoverAddedCart();
          });
        }
        try {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response, () => this.recoverAddedCart());
          });
        } catch (error) {
          console.error(error);
          this.recoverAddedCart();
        }
      }

      recoverAddedCart() {
        if (this.cartRecoveryPending) return;
        this.cartRecoveryPending = true;
        this.error = false;
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        const root = window.Shopify?.routes?.root || '/';
        window.location = window.routes?.cart_url || `${root.replace(/\/$/, '')}/cart`;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.errorMessage = document.createElement('span');
          this.errorMessage.className = 'product-form__error-message';
          this.errorMessageWrapper.append(this.errorMessage);
          this.prepend(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
